import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Button from "../Button/Button"
import Input from "../Input/Input"
import {
  startCardVerification,
  recordCardVerificationAnswers,
  getCardVerificationStatus,
  selectCardVerification,
  selectCardVerificationLoading,
  selectCardVerificationError,
} from "../../features/cardVerification/cardVerification"

const YesNo = ({ label, value, onChange, disabled, testId }) => (
  <div className="flex items-center justify-between w-full py-2" data-testid={testId}>
    <div className="text-text-primary text-sm">{label}</div>
    <div className="flex gap-2">
      <span data-testid={`${testId}-yes`}>
        <Button size="sm" type={value === true ? "" : "outlined"} disabled={disabled} onClick={() => onChange(true)}>
          Yes
        </Button>
      </span>
      <span data-testid={`${testId}-no`}>
        <Button size="sm" type={value === false ? "" : "outlined"} disabled={disabled} onClick={() => onChange(false)}>
          No
        </Button>
      </span>
    </div>
  </div>
)

const AttemptList = ({ attempts }) => {
  if (!attempts?.length) return null

  return (
    <ul className="w-full text-xs text-text-secondary flex flex-col gap-1" data-testid="verification-attempts">
      {attempts.map((attempt, index) => (
        <li key={`${attempt.gateway}-${attempt.processor || index}`} className="flex justify-between">
          <span>{attempt.processor || attempt.gateway}</span>
          <span>
            {attempt.outcome}
            {attempt.reasonText ? ` — ${attempt.reasonText}` : ""}
            {attempt.reversal?.completed ? ` — ${attempt.reversal.operation}ed` : ""}
          </span>
        </li>
      ))}
    </ul>
  )
}

const toCents = (typed) => {
  const cleaned = String(typed).trim().replace(/^\$/, '')
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null
  return Math.round(Number(cleaned) * 100)
}

const CardVerificationPanel = ({ sessionToken, billingAccountId, affiliateId, onVerified }) => {
  const dispatch = useDispatch()
  const verification = useSelector(selectCardVerification)
  const loading = useSelector(selectCardVerificationLoading)
  const error = useSelector(selectCardVerificationError)

  const [clientSeesDebit, setClientSeesDebit] = useState(null)
  const [typedAmount, setTypedAmount] = useState("")

  // Recover an existing verification rather than starting one. A refresh mid-call must never
  // produce a second real charge on the client's card.
  useEffect(() => {
    if (sessionToken || billingAccountId) dispatch(getCardVerificationStatus({ sessionToken, billingAccountId }))
  }, [dispatch, sessionToken, billingAccountId])

  useEffect(() => {
    if (verification?.verdict === "passed") onVerified?.(verification)
  }, [verification, onVerified])

  if (!sessionToken && !billingAccountId) return null

  const status = verification?.status
  const awaitingAnswers = status === "awaitingConfirmation"
  const finished = status === "passed" || status === "failed" || status === "parked"

  const enteredAmountCents = toCents(typedAmount)

  const start = () => dispatch(startCardVerification({ sessionToken, billingAccountId, affiliateId }))

  const submitAnswers = (affiliateVerdict) => dispatch(recordCardVerificationAnswers({
    sessionToken,
    billingAccountId,
    verificationId: verification.verificationId,
    clientSeesDebit: clientSeesDebit === true,
    enteredAmountCents: enteredAmountCents === null ? -1 : enteredAmountCents,
    affiliateVerdict,
  }))

  return (
    <div
      className="flex flex-col items-start gap-4 self-stretch pt-6 pb-8 px-6 rounded-2xl border border-divider bg-paper"
      data-testid="card-verification-panel"
    >
      <div className="text-text-secondary font-sans text-xs font-bold leading-[150%] uppercase">
        Card Verification
      </div>

      {!verification && (
        <>
          <div className="text-text-secondary text-sm">
            A small temporary charge confirms the card works. It is reversed immediately.
          </div>
          <span data-testid="start-verification">
            <Button size="lg" disabled={loading} onClick={start}>
              {loading ? "Verifying…" : "Start verification"}
            </Button>
          </span>
        </>
      )}

      {verification && (
        <>
          <AttemptList attempts={verification.attempts} />

          {awaitingAnswers && (
            <>
              <YesNo
                label="Does the client see the charge?"
                value={clientSeesDebit}
                onChange={setClientSeesDebit}
                disabled={loading}
                testId="answer-sees-debit"
              />
              <div className="w-full flex flex-col gap-1" data-testid="answer-amount-entry">
                <Input
                  label="Amount the client reads from their statement"
                  value={typedAmount}
                  onChange={(event) => setTypedAmount(event?.target?.value ?? event)}
                  disabled={loading}
                  placeholder="0.00"
                />
                <span className="text-text-secondary text-xs">
                  {`Type what they read. ${verification.attemptsRemaining ?? 0} attempt(s) left.`}
                </span>
                {verification.amountMatches === false && (
                  <span className="text-error text-xs" data-testid="amount-mismatch">
                    That is not the amount we charged. Ask them to read it again.
                  </span>
                )}
              </div>
              <div className="flex gap-3 w-full">
                <span data-testid="confirm-verified">
                  <Button
                    size="lg"
                    disabled={loading || clientSeesDebit !== true || enteredAmountCents === null}
                    onClick={() => submitAnswers(true)}
                  >
                    Verification succeeded
                  </Button>
                </span>
                <span data-testid="confirm-failed">
                  <Button size="lg" type="outlined" disabled={loading} onClick={() => submitAnswers(false)}>
                    Verification failed
                  </Button>
                </span>
              </div>
            </>
          )}

          {status === "failed" && (
            <div className="text-error text-sm" data-testid="verification-failed">
              This card could not be verified. Use a bank account instead, or try a different card.
            </div>
          )}

          {status === "parked" && (
            <div className="text-error text-sm" data-testid="verification-parked">
              Verification could not be completed. Your representative will follow up — please do not retry.
            </div>
          )}

          {status === "passed" && (
            <div className="text-success text-sm" data-testid="verification-passed">
              Card verified{verification.approvedProcessor ? ` via ${verification.approvedProcessor}` : ""}.
            </div>
          )}

          {finished && status !== "passed" && verification.attempts?.some(a => a.outcome === "approved") && (
            <div className="text-text-secondary text-xs">
              The charge has been reversed.
            </div>
          )}
        </>
      )}

      {error && (
        <div className="text-error text-sm" data-testid="verification-error">
          {error.message}
        </div>
      )}
    </div>
  )
}

export default CardVerificationPanel
